import {ReportRecord} from "@/shared/timr";

export interface DataTableBulkSelectorConfig {
	dataTable: DataTables.Api;
}

interface DataTableBulkSelectorEntry<T> {
	selected: boolean;
	recording: T;
}

/**
 * Selection is keyed by the row id type `ID` (`number` for recordings, `string`
 * for external users), inferred from `T` by default.
 */
export class DataTableBulkSelector<T extends { id: ID }, ID extends string | number = T["id"]> {

	public static EVENT_BULK_SELECTION_CHANGED = "bulkSelectionChanged";
	public static EVENT_BULK_SELECTION_TOGGLED = "bulkSelectionToggled";
	protected entries = new Map<ID, DataTableBulkSelectorEntry<T>>();
	protected allSelected = false;
	protected totalEntriesCount = 0;
	protected selectedEntriesCount = 0;

	constructor(public config: DataTableBulkSelectorConfig) {
		this.initialize();
	}

	public isSelected(id: ID): boolean {
		const entry = this.entries.get(id);
		return entry != null ? entry.selected : this.allSelected;
	}

	public hasSelectedEntries(): boolean {
		return this.selectedEntriesCount > 0;
	}

	public getSelectedEntriesCount(): number {
		return this.selectedEntriesCount;
	}

	public isAllSelected(): boolean {
		return this.allSelected;
	}

	public isAllEntriesSelected(): boolean {
		return (this.selectedEntriesCount === this.totalEntriesCount);
	}

	public isNoneSelected(): boolean {
		return this.selectedEntriesCount === 0;
	}

	public getIdsToInclude(): ID[] | null {
		return this.allSelected ? null : this.getSelectedIds();
	}

	public getIdsToExclude(): ID[] | null {
		return this.allSelected ? this.getSelectedIds() : null;
	}

	public reset() {
		this.entries.clear();
		this.totalEntriesCount = 0;
		this.selectedEntriesCount = 0;
		this.allSelected = false;
		this.resetSums();
		this.unSelectAllCheckbox();
		this.onSelectionChanged();
	}

	protected resetSums(): void {
		// Extended by subclasses to reset their sum fields
	}

	protected select(recordings: T[], selected: boolean) {
		for (const recording of recordings) {
			const entry = this.entries.get(recording.id);
			const entryExists = entry !== undefined;
			const changed = entryExists && entry.selected !== selected;

			if (selected && entryExists && entry.selected) {
				this.onRecordDataChanged(recording);
			}

			this.entries.set(recording.id, {recording: recording, selected: selected});

			if (selected) {
				if (changed || (!entryExists && !this.allSelected)) {
					this.selectedEntriesCount = Math.min(this.totalEntriesCount, this.selectedEntriesCount + 1);
					this.onEntryAdded(recording);
				}
			} else {
				if (changed || (!entryExists && this.allSelected)) {
					this.selectedEntriesCount = Math.max(0, this.selectedEntriesCount - 1);
					this.onEntryRemoved(recording);
				}
			}
			this.onSelectionToggled(recording, selected);
		}
		if (!this.hasSelectedEntries()) {
			this.allSelected = false;
		}
		this.onSelectionChanged();
	}

	/** Called when an entry transitions to selected. Override to track custom sums. */
	protected onEntryAdded(recording: T): void {
	}

	/** Called when an entry transitions to deselected. Override to track custom sums. */
	protected onEntryRemoved(recording: T): void {
	}

	/**
	 * Called when a recording is re-selected while already selected — the data may have changed.
	 * Override to reconcile sum deltas between the stored entry and the incoming recording.
	 */
	protected onRecordDataChanged(recording: T): void {
	}

	public selectAll() {
		this.config.dataTable.rows().select();
		this.entries.clear();
		this.selectedEntriesCount = this.totalEntriesCount;
		this.allSelected = true;
		this.setAllSelectedSums();
		this.onSelectionChanged();
	}

	public selectNone() {
		this.config.dataTable.rows().deselect();
		this.entries.clear();
		this.selectedEntriesCount = 0;
		this.allSelected = false;
		this.resetSums();
		this.onSelectionChanged();
	}

	public selectAllCheckbox() {
		const tableContainer = $(this.config.dataTable.table("").container());
		tableContainer.find("th.select-all input.select-all-checkbox").prop("checked", true);
	}

	public unSelectAllCheckbox() {
		const tableContainer = $(this.config.dataTable.table("").container());
		tableContainer.find("th.select-all input.select-all-checkbox").prop("checked", false);
	}

	protected initAllSelectedSum(_dataTableJsonData: unknown): void {
		// No server-side sums by default; sum-tracking selectors override this.
	}

	/** Override to apply all-selected sum snapshots when selectAll() is called. */
	protected setAllSelectedSums(): void {
	}

	private initialize() {
		this.config.dataTable.on("select", (e, dt: DataTables.Api, type, indexes) => {
			if (!dt) return;
			const recordings = dt.rows(indexes).data().toArray() as T[];
			this.select(recordings, true);
		});
		this.config.dataTable.on("deselect", (e, dt: DataTables.Api, type, indexes) => {
			if (!dt) return;
			const recordings = dt.rows(indexes).data().toArray() as T[];
			this.select(recordings, false);
		});

		const tableContainer = $(this.config.dataTable.table("").container());
		const selectAllCheckbox = tableContainer.find("th.select-all input.select-all-checkbox");

		selectAllCheckbox.on("change", (event) => {
			const checked = $(event.currentTarget).prop("checked") as boolean;
			if (checked) {
				this.selectAll();
			} else {
				this.selectNone();
			}
		});

		tableContainer.find("th.select-all").on("click", (event) => {
			const target = $(event.target);
			if (target.is("input.select-all-checkbox")) {
				return;
			}
			const checkbox = tableContainer.find("th.select-all input.select-all-checkbox");
			checkbox.prop("checked", !checkbox.prop("checked")).trigger("change");
		});

		this.config.dataTable.on("draw", (e, settings: DataTables.SettingsLegacy, type, indexes) => {
			const newTotalEntriesCount = settings.fnRecordsDisplay();
			const dataTableJsonData = this.config.dataTable.ajax.json();
			this.initAllSelectedSum(dataTableJsonData);
			if (this.totalEntriesCount !== newTotalEntriesCount) {
				this.reset();
			}
			this.totalEntriesCount = newTotalEntriesCount;
		});
	}

	private getSelectedIds(): ID[] {
		const ids: ID[] = [];
		this.entries.forEach((entry, id) => {
			if (entry.selected !== this.allSelected) {
				ids.push(id);
			}
		});
		return ids;
	}

	protected onSelectionChanged() {
		const recordings = this.config.dataTable.rows({selected: true}).data();
		$(this).triggerHandler(DataTableBulkSelector.EVENT_BULK_SELECTION_CHANGED, [recordings]);
	}

	protected onSelectionToggled(recording: T, selected: boolean) {
		$(this).triggerHandler(DataTableBulkSelector.EVENT_BULK_SELECTION_TOGGLED, [recording, selected]);
	}
}

/**
 * Base class for bulk selectors that track duration sums for recorded entries (working time,
 * project time, drive log). Subclasses can extend further by overriding `sumAdditionalParams`.
 */
export abstract class RecordedEntryDataTableBulkSelector<T extends ReportRecord> extends DataTableBulkSelector<T> {

	protected durationNetSum = 0;
	protected durationGrossSum = 0;
	protected breakTimeSum = 0;

	/** Snapshots of sums when all entries are selected — set from the server-side totals. Milliseconds. */
	protected allSelectedDurationNetSum = 0;
	protected allSelectedDurationGrossSum = 0;
	protected allSelectedBreakTimeSum = 0;

	public getSums() {
		return {
			durationNetSum: this.durationNetSum,
			durationGrossSum: this.durationGrossSum,
			breakTimeSum: this.breakTimeSum
		};
	}

	protected override resetSums(): void {
		this.durationNetSum = 0;
		this.durationGrossSum = 0;
		this.breakTimeSum = 0;
	}

	protected override setAllSelectedSums(): void {
		this.durationNetSum = this.allSelectedDurationNetSum;
		this.durationGrossSum = this.allSelectedDurationGrossSum;
		this.breakTimeSum = this.allSelectedBreakTimeSum;
	}

	protected override onEntryAdded(recording: T): void {
		this.durationNetSum += recording.durationNet;
		this.durationGrossSum += recording.durationGross;
		this.breakTimeSum += recording.breakTime * 60000;
		this.sumAdditionalParams(recording, false);
	}

	protected override onEntryRemoved(recording: T): void {
		this.durationNetSum -= recording.durationNet;
		this.durationGrossSum -= recording.durationGross;
		this.breakTimeSum -= recording.breakTime * 60000;
		this.sumAdditionalParams(recording, true);
	}

	protected override onRecordDataChanged(recording: T): void {
		const old = this.entries.get(recording.id).recording;
		if (old.durationNet !== recording.durationNet) {
			this.durationNetSum += recording.durationNet - old.durationNet;
		}
		if (old.durationGross !== recording.durationGross) {
			this.durationGrossSum += recording.durationGross - old.durationGross;
		}
		if (old.breakTime !== recording.breakTime) {
			this.breakTimeSum += (recording.breakTime - old.breakTime) * 60000;
		}
	}

	/**
	 * Override in subclasses to add/subtract type-specific sum fields (e.g. distance, billable duration).
	 * Called from {@link onEntryAdded} and {@link onEntryRemoved}.
	 */
	protected sumAdditionalParams(_recording: T, _subtract: boolean): void {
	}
}
